import { createClient } from '@supabase/supabase-js';
import { dev } from '$app/environment';

/**
 * Get the Supabase client
 * Uses the globally initialized client if available, otherwise creates a new one
 */
export function getSupabaseClient() {
  // If we're on the client side and the global client is available, use it
  if (typeof window !== 'undefined' && window.__SUPABASE_CLIENT) {
    return window.__SUPABASE_CLIENT;
  }
  
  // Otherwise, create a new client
  const url = typeof window !== 'undefined' && window.env?.PUBLIC_SUPABASE_URL 
    ? window.env.PUBLIC_SUPABASE_URL 
    : process.env.PUBLIC_SUPABASE_URL || 'https://supabase.hollas.net';
    
  const key = typeof window !== 'undefined' && window.env?.PUBLIC_SUPABASE_ANON_KEY 
    ? window.env.PUBLIC_SUPABASE_ANON_KEY 
    : process.env.PUBLIC_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6InN1cGFiYXNlLWRlbW8iLCJpYXQiOjE3Mzk4MDIxNTIsImV4cCI6MTc3MTMzODE1Mn0.GBwtLC3ztyqfc39JO5BbFEbfEyC5o_38cDbpRZjvKY8';
  
  if (dev) {
    console.log(`Creating new Supabase client with URL: ${url.substring(0, 25)}...`);
  }
  
  return createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true
    }
  });
}

// Export a singleton instance that uses our getSupabaseClient function
export const supabase = getSupabaseClient();

// Helper function to check auth status
export const isAuthenticated = async () => {
  const client = getSupabaseClient();
  try {
    const { data } = await client.auth.getSession();
    return !!data.session;
  } catch (e) {
    return false;
  }
};

// Status function for health checks and API status endpoint
export const getSupabaseStatus = () => {
  // Get current credentials
  const url = typeof window !== 'undefined' && window.env?.PUBLIC_SUPABASE_URL 
    ? window.env.PUBLIC_SUPABASE_URL 
    : process.env.PUBLIC_SUPABASE_URL || '';
    
  const key = typeof window !== 'undefined' && window.env?.PUBLIC_SUPABASE_ANON_KEY 
    ? window.env.PUBLIC_SUPABASE_ANON_KEY 
    : process.env.PUBLIC_SUPABASE_ANON_KEY || '';

  return {
    hasUrl: Boolean(url),
    hasKey: Boolean(key),
    url: url ? `${url.substring(0, 8)}...` : 'missing',
    env: typeof process !== 'undefined' ? process.env.NODE_ENV : 'browser',
    timestamp: new Date().toISOString()
  };
};

// TypeScript types for global objects
declare global {
  interface Window {
    env?: Record<string, string>;
    supabase?: {
      createClient: typeof createClient;
    };
    __SUPABASE_CLIENT?: ReturnType<typeof createClient>;
  }
}

if (dev) {
  console.log('Supabase module loaded');
}
